import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import '../../styles/PropertiesScreen.css';

const AddServiceModal = ({ isOpen, onClose, property, onSave }) => {
    const [products, setProducts] = useState([]);
    const [activeProductIds, setActiveProductIds] = useState([]);
    const [selectedServices, setSelectedServices] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedServices({});
            setError('');
            setIsLoading(true);

            const fetchData = async () => {
                try {
                    const [productData, existingSubs] = await Promise.all([
                        api.getProducts(),
                        api.getPropertySubscriptions(property.id)
                    ]);
                    
                    setProducts(productData);
                    setActiveProductIds(existingSubs.map(sub => sub.product_id));
                } catch (err) {
                    setError('Failed to load required data.');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchData();
        }
    }, [isOpen, property.id]);

    const handleCheckboxChange = (e) => {
        const { name: productId, checked } = e.target;
        const today = new Date().toISOString().split('T')[0];
        setSelectedServices(prev => ({
            ...prev,
            [productId]: checked ? { domainName: '', startDate: today } : false,
        }));
    };

    const handleDateChange = (productId, startDate) => {
        setSelectedServices(prev => ({
            ...prev,
            [productId]: { ...prev[productId], startDate },
        }));
    };

    const handleDomainChange = (productId, domainName) => {
        setSelectedServices(prev => ({
            ...prev,
            [productId]: { ...prev[productId], domainName },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const servicesToSubmit = Object.entries(selectedServices)
            .filter(([, value]) => value !== false)
            .map(([productId, data]) => ({
                productId: parseInt(productId),
                startDate: data.startDate,
                domainName: data.domainName || null,
            }));

        if (servicesToSubmit.length === 0) {
            setError('You must select at least one service.');
            return;
        }

        setIsSubmitting(true);
        try {
            await onSave({
                propertyId: property.id,
                services: servicesToSubmit,
            });
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to add services.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    if (!isOpen) return null;

   return (
    <div className="modal-overlay">
        <div className="modal-content">
            <h2>Add Services to "{property.name}"</h2>
            
            {isLoading ? <p>Loading services...</p> : 
                <form onSubmit={handleSubmit}>
                    {error && <div className="form-error-banner">{error}</div>}
                    
                    <div className="form-group">
                        <label className="form-label">Available Services</label>
                        <div className="service-list">
                            {products.map(product => {
                                const isSubscribed = activeProductIds.includes(product.id);
                                return (
                                    <div key={product.id} className={`service-item ${isSubscribed ? 'disabled' : ''}`}>
                                        <div className="service-details">
                                            <span className="service-name">{product.name}</span>
                                        </div>
                                        
                                        <input
                                            type="checkbox"
                                            id={`product-${product.id}`}
                                            name={product.id}
                                            checked={!!selectedServices[product.id]}
                                            onChange={handleCheckboxChange}
                                            disabled={isSubscribed}
                                        />

                                        {/* FIX: Add the inputs for date and domain */}
                                        {selectedServices[product.id] && (
                                            <div className="service-inputs">
                                                <input 
                                                    type="date"
                                                    className="form-input"
                                                    value={selectedServices[product.id].startDate}
                                                    onChange={e => handleDateChange(product.id, e.target.value)}
                                                    required
                                                />
                                                {product.type === 'domain_registration' && (
                                                    <input 
                                                        type="text"
                                                        className="form-input"
                                                        placeholder="Domain Name"
                                                        value={selectedServices[product.id].domainName}
                                                        onChange={e => handleDomainChange(product.id, e.target.value)}
                                                        required
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="button button-secondary" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button type="submit" className="button action-button" disabled={isSubmitting || isLoading}>
                            {isSubmitting ? 'Saving...' : 'Add Services & Invoices'}
                        </button>
                    </div>
                </form>
            }
        </div>
    </div>
    );
};

export default AddServiceModal;